// ============================================================
// Multi-turn slot-filling flows
// Each flow declares an ordered list of slots to fill, with
// extractors and validators. The runner asks for the next
// missing slot, accepts the user's reply, validates, then
// continues until all slots are filled, then "executes".
// ============================================================
import type { AgentMemory, AgentWidget, PendingFlow, UIAction } from '@/agent/agentStore';
import { extractWOId } from '@/agent/fakeBrain';

export interface FlowResponse {
  reply: string;
  widgets?: AgentWidget[];
  uiActions?: UIAction[];
  memoryPatch?: Partial<AgentMemory>;
  done?: boolean;
}

export interface SlotDef {
  name: string;
  prompt: string; // question to ask user when slot is missing
  /** Extract value from raw user message; return undefined if no match */
  extract: (msg: string) => unknown | undefined;
  /** Optional validation; return error string to reject */
  validate?: (value: unknown) => string | null;
  /** Optional widget to render alongside the prompt (e.g. action_buttons with quick options) */
  quickReplies?: Array<{ label: string; value: string }>;
}

export interface FlowDef {
  id: string;
  /** Patterns that *start* this flow */
  triggers: RegExp[];
  /** Friendly title shown when starting */
  startReply: string;
  slots: SlotDef[];
  /** Final action when all slots are filled */
  execute: (collected: Record<string, unknown>) => FlowResponse;
}

// ------------------------------------------------------------
// Slot extractors
// ------------------------------------------------------------
const SEVERITY_REGEX = /\b(critical|crítica|critica|major|mayor|minor|menor)\b/i;
const PRIORITY_REGEX = /\b(urgent|urgente|high|alta|normal|low|baja)\b/i;
const DURATION_REGEX = /\b(\d{1,3})\s*(min|m|minutos?|h|hours?|horas?)\b/i;
const REASON_REGEX = /^(?!\s*(no|cancelar|cancel|salir|exit|abort)\s*$).{3,}$/i;

function normalizeSeverity(v: string): string {
  const s = v.toLowerCase();
  if (s.startsWith('crit') || s.startsWith('crít')) return 'CRITICAL';
  if (s.startsWith('maj') || s.startsWith('may')) return 'MAJOR';
  return 'MINOR';
}

function normalizePriority(v: string): string {
  const s = v.toLowerCase();
  if (s.startsWith('urg')) return 'URGENT';
  if (s.startsWith('hi') || s.startsWith('alt')) return 'HIGH';
  if (s.startsWith('lo') || s.startsWith('baj')) return 'LOW';
  return 'NORMAL';
}

// ------------------------------------------------------------
// Flow definitions
// ------------------------------------------------------------
export const FLOWS: FlowDef[] = [
  // ------- Crear NC -------
  {
    id: 'create_nc',
    triggers: [
      /\b(crear|abrir|registrar|nueva|create|open|log)\s+(una\s+)?(nc|no\s*conformidad|defecto)\b/i,
    ],
    startReply: 'Vamos a crear una NC. Te haré 3 preguntas rápidas.',
    slots: [
      {
        name: 'wo',
        prompt: '¿En qué orden de trabajo? (ej. WO-2024-0892)',
        extract: (msg) => extractWOId(msg) ?? undefined,
        validate: (v) => (typeof v === 'string' && /^WO-\d{4}/.test(v) ? null : 'Necesito un código WO-YYYY-NNNN.'),
      },
      {
        name: 'severity',
        prompt: '¿Severidad? (CRITICAL · MAJOR · MINOR)',
        extract: (msg) => {
          const m = msg.match(SEVERITY_REGEX);
          return m ? normalizeSeverity(m[1]) : undefined;
        },
        quickReplies: [
          { label: 'CRITICAL', value: 'CRITICAL' },
          { label: 'MAJOR', value: 'MAJOR' },
          { label: 'MINOR', value: 'MINOR' },
        ],
      },
      {
        name: 'description',
        prompt: 'Descripción breve del defecto.',
        extract: (msg) => {
          const trimmed = msg.trim();
          return REASON_REGEX.test(trimmed) ? trimmed : undefined;
        },
        validate: (v) =>
          typeof v === 'string' && v.length >= 3 ? null : 'La descripción debe tener al menos 3 caracteres.',
      },
    ],
    execute: (c) => {
      const descToken = encodeURIComponent(String(c.description).slice(0, 80));
      return {
        reply: `Voy a crear NC en ${c.wo} con severidad ${c.severity}. Confirma:`,
        uiActions: [
          { type: 'navigate', to: '/quality/board' },
          {
            type: 'confirm',
            prompt: `¿Crear NC ${c.severity} en ${c.wo}?\n"${String(c.description).slice(0, 80)}"`,
            onConfirmToken: `createnc_${c.wo}_${c.severity}_${descToken}`,
          },
        ],
        done: true,
      };
    },
  },

  // ------- Programar parada -------
  {
    id: 'schedule_stop',
    triggers: [
      /\b(programar|planificar|schedule)\s+(una\s+)?parada\b/i,
      /\bparada\s+(programada|planificada)\b/i,
    ],
    startReply: 'Vamos a programar una parada. Necesito 3 datos.',
    slots: [
      {
        name: 'station',
        prompt: '¿Qué estación o máquina? (ej. M-012, Línea A)',
        extract: (msg) => {
          const m = msg.match(/\b([ML]-\d{2,3}|L[ií]nea\s+[A-Z])\b/i);
          return m ? m[1].toUpperCase() : undefined;
        },
      },
      {
        name: 'duration',
        prompt: '¿Cuánto tiempo? (ej. 30 min, 2 h)',
        extract: (msg) => {
          const m = msg.match(DURATION_REGEX);
          if (!m) return undefined;
          const n = Number(m[1]);
          const unit = m[2].toLowerCase();
          const minutes = unit.startsWith('h') ? n * 60 : n;
          return `${minutes}m`;
        },
        quickReplies: [
          { label: '15 min', value: '15 min' },
          { label: '30 min', value: '30 min' },
          { label: '1 h', value: '1 h' },
        ],
      },
      {
        name: 'reason',
        prompt: 'Motivo de la parada.',
        extract: (msg) => (REASON_REGEX.test(msg.trim()) ? msg.trim() : undefined),
      },
    ],
    execute: (c) => {
      // Parse minutes from "30m" / "60m" etc.
      const minutes = Number(String(c.duration).replace(/[^\d]/g, '')) || 30;
      const machineId = String(c.station).replace(/\s+/g, '-');
      return {
        reply: `Voy a programar parada en ${c.station} (${minutes} min). Confirma:`,
        uiActions: [
          {
            type: 'confirm',
            prompt: `¿Programar parada en ${c.station} de ${minutes} min?\nMotivo: "${c.reason}"`,
            onConfirmToken: `stop_${machineId}_${minutes}`,
          },
        ],
        done: true,
      };
    },
  },

  // ------- Cambiar prioridad WO -------
  {
    id: 'change_wo_priority',
    triggers: [
      /\b(cambiar|change|set)\s+prioridad\b/i,
      /\bprioridad\s+(de\s+)?(la\s+)?(orden|wo)\b/i,
    ],
    startReply: 'Cambio de prioridad. Dime los datos:',
    slots: [
      {
        name: 'wo',
        prompt: '¿Qué orden? (ej. WO-2024-0892)',
        extract: (msg) => extractWOId(msg) ?? undefined,
        validate: (v) => (typeof v === 'string' && /^WO-\d{4}/.test(v) ? null : 'Formato WO-YYYY-NNNN.'),
      },
      {
        name: 'priority',
        prompt: '¿Nueva prioridad? (URGENT · HIGH · NORMAL · LOW)',
        extract: (msg) => {
          const m = msg.match(PRIORITY_REGEX);
          return m ? normalizePriority(m[1]) : undefined;
        },
        quickReplies: [
          { label: 'URGENT', value: 'URGENT' },
          { label: 'HIGH', value: 'HIGH' },
          { label: 'NORMAL', value: 'NORMAL' },
          { label: 'LOW', value: 'LOW' },
        ],
      },
    ],
    execute: (c) => ({
      reply: `Voy a cambiar prioridad de ${c.wo} a ${c.priority}. Confirma:`,
      uiActions: [
        { type: 'navigate', to: '/production/dispatch' },
        { type: 'select_item', view: 'dispatch_board', id: String(c.wo) },
        { type: 'highlight', selector: `[data-id="${c.wo}"]`, durationMs: 2500 },
        {
          type: 'confirm',
          prompt: `¿Cambiar prioridad de ${c.wo} a ${c.priority}?`,
          onConfirmToken: `prio_${c.wo}_${c.priority}`,
        },
      ],
      memoryPatch: { lastWorkOrder: String(c.wo) },
      done: true,
    }),
  },

  // ------- Disposicionar NC -------
  {
    id: 'disposition_nc',
    triggers: [
      /\b(disposicion(ar)?|dispose|resolver)\s+(la\s+)?(nc[-\s]?\d+)/i,
      /\b(rework|scrap|release|liberar)\s+(la\s+)?(nc[-\s]?\d+)/i,
    ],
    startReply: 'Voy a disposicionar la NC. Necesito 2 datos.',
    slots: [
      {
        name: 'nc_id',
        prompt: '¿Qué NC? (ej. NC-45)',
        extract: (msg) => {
          const m = msg.match(/NC[-\s]?(\d{1,5})/i);
          return m ? `NC-${m[1]}` : undefined;
        },
        validate: (v) => (typeof v === 'string' && /^NC-\d/.test(v) ? null : 'Formato NC-xxx.'),
      },
      {
        name: 'disposition',
        prompt: '¿Qué disposición? (REWORK · SCRAP · RELEASE)',
        extract: (msg) => {
          const m = msg.toLowerCase();
          if (/rework|reproceso/.test(m)) return 'REWORK';
          if (/scrap|merma|descart/.test(m)) return 'SCRAP';
          if (/release|liberar/.test(m)) return 'RELEASE';
          return undefined;
        },
        quickReplies: [
          { label: 'REWORK', value: 'REWORK' },
          { label: 'SCRAP', value: 'SCRAP' },
          { label: 'RELEASE', value: 'RELEASE' },
        ],
      },
    ],
    execute: (c) => ({
      reply: `Confirma disposición de ${c.nc_id} como ${c.disposition}:`,
      uiActions: [
        { type: 'navigate', to: '/quality/board' },
        { type: 'select_item', view: 'quality_board', id: String(c.nc_id) },
        { type: 'highlight', selector: `[data-id="${c.nc_id}"]`, durationMs: 2500 },
        {
          type: 'confirm',
          prompt: `¿Confirmas disposición ${c.disposition} para ${c.nc_id}?`,
          onConfirmToken: `disp_${c.nc_id}_${c.disposition}`,
        },
      ],
      done: true,
    }),
  },

  // ------- Desplegar modelo -------
  {
    id: 'deploy_model',
    triggers: [
      /\b(desplegar|deploy|promote|promocionar)\s+(el\s+)?modelo\b/i,
      /\bmodel(o)?\s+(a|to)\s+(producci[oó]n|production|staging)\b/i,
    ],
    startReply: 'Vamos a desplegar un modelo. Dos datos:',
    slots: [
      {
        name: 'model_id',
        prompt: '¿Qué modelo? (ej. YOLO11-v4, EffNetV2-B3)',
        extract: (msg) => {
          const m = msg.match(/\b([A-Z][A-Za-z0-9]+[-\s]?v?\d+(?:[.-][A-Za-z0-9]+)?)\b/);
          return m ? m[1].replace(/\s+/g, '-') : undefined;
        },
      },
      {
        name: 'target_env',
        prompt: '¿A dónde? (staging o production)',
        extract: (msg) => {
          const m = msg.toLowerCase();
          if (/prod/.test(m)) return 'production';
          if (/stag/.test(m)) return 'staging';
          return undefined;
        },
        quickReplies: [
          { label: 'staging', value: 'staging' },
          { label: 'production', value: 'production' },
        ],
      },
    ],
    execute: (c) => ({
      reply: `Pidiendo confirmación para desplegar ${c.model_id} → ${c.target_env}:`,
      uiActions: [
        { type: 'navigate', to: '/mlops/model-factory' },
        { type: 'select_item', view: 'model_factory', id: String(c.model_id) },
        {
          type: 'confirm',
          prompt: `¿Desplegar ${c.model_id} a ${c.target_env}? Esta acción afecta a producción.`,
          onConfirmToken: `deploy_${c.model_id}_${c.target_env}`,
        },
      ],
      done: true,
    }),
  },

  // ------- Asignar WO a estación -------
  {
    id: 'assign_wo',
    triggers: [
      /\b(asignar|assign|asigna)\s+(la\s+)?(orden|wo|WO-\d{4})/i,
    ],
    startReply: 'Vamos a asignar una orden a estación. Dos datos:',
    slots: [
      {
        name: 'wo',
        prompt: '¿Qué orden? (ej. WO-2024-0892)',
        extract: (msg) => extractWOId(msg) ?? undefined,
        validate: (v) => (typeof v === 'string' && /^WO-\d{4}/.test(v) ? null : 'Formato WO-YYYY-NNNN.'),
      },
      {
        name: 'station',
        prompt: '¿Qué estación? (ej. ST-A1, ST-B3)',
        extract: (msg) => {
          const m = msg.match(/\bST-[A-Z]\d{1,2}\b/i);
          return m ? m[0].toUpperCase() : undefined;
        },
      },
    ],
    execute: (c) => ({
      reply: `Asignando ${c.wo} a ${c.station}:`,
      uiActions: [
        { type: 'navigate', to: '/production/dispatch' },
        { type: 'select_item', view: 'dispatch_board', id: String(c.wo) },
        {
          type: 'confirm',
          prompt: `¿Confirmas asignar ${c.wo} a la estación ${c.station}?`,
          onConfirmToken: `assign_${c.wo}_${c.station}`,
        },
      ],
      memoryPatch: { lastWorkOrder: String(c.wo) },
      done: true,
    }),
  },
];

// ------------------------------------------------------------
// Cancel detection
// ------------------------------------------------------------
const CANCEL_REGEX = /^\s*(cancelar|cancel|abort|salir|exit|d[eé]jalo|stop)\s*\.?$/i;

export function isCancelMessage(msg: string): boolean {
  return CANCEL_REGEX.test(msg);
}

// ------------------------------------------------------------
// Find a flow trigger in a message
// ------------------------------------------------------------
export function findFlowTrigger(msg: string): FlowDef | null {
  for (const flow of FLOWS) {
    for (const pattern of flow.triggers) {
      if (pattern.test(msg)) return flow;
    }
  }
  return null;
}

export function getFlowById(id: string): FlowDef | null {
  return FLOWS.find((f) => f.id === id) ?? null;
}

// ------------------------------------------------------------
// Build a "ask for next slot" response
// ------------------------------------------------------------
export function askNextSlot(flow: FlowDef, collected: Record<string, unknown>): {
  slot: SlotDef | null;
  reply: string;
  widgets?: AgentWidget[];
} {
  const next = flow.slots.find((s) => collected[s.name] === undefined);
  if (!next) return { slot: null, reply: '' };
  const widgets: AgentWidget[] | undefined = next.quickReplies
    ? [
        {
          type: 'action_buttons',
          items: next.quickReplies.map((qr) => ({ label: qr.label, sendText: qr.value })),
        },
      ]
    : undefined;
  return { slot: next, reply: next.prompt, widgets };
}

// ------------------------------------------------------------
// Process a user message in the context of an active flow
// ------------------------------------------------------------
export function advanceFlow(pending: PendingFlow, message: string): FlowResponse {
  if (isCancelMessage(message)) {
    return {
      reply: 'Flujo cancelado. ¿En qué más te ayudo?',
      memoryPatch: { pendingFlow: null },
      done: true,
    };
  }

  const flow = getFlowById(pending.id);
  if (!flow) {
    return { reply: 'Flujo desconocido. Lo cancelo.', memoryPatch: { pendingFlow: null }, done: true };
  }

  const collected = { ...pending.collected };
  const targetSlot = pending.currentSlot
    ? flow.slots.find((s) => s.name === pending.currentSlot)
    : flow.slots.find((s) => collected[s.name] === undefined);

  if (!targetSlot) {
    // All slots filled → execute
    const result = flow.execute(collected);
    return {
      ...result,
      memoryPatch: { ...(result.memoryPatch ?? {}), pendingFlow: null },
      done: true,
    };
  }

  const value = targetSlot.extract(message);
  if (value === undefined) {
    return {
      reply: `No te he entendido. ${targetSlot.prompt}\n(escribe "cancelar" para abortar)`,
      widgets: targetSlot.quickReplies
        ? [{ type: 'action_buttons', items: targetSlot.quickReplies.map((qr) => ({ label: qr.label, sendText: qr.value })) }]
        : undefined,
    };
  }

  const validationError = targetSlot.validate?.(value);
  if (validationError) {
    return { reply: `${validationError} ${targetSlot.prompt}` };
  }

  collected[targetSlot.name] = value;

  // Look for next missing slot
  const nextMissing = flow.slots.find((s) => collected[s.name] === undefined);
  if (!nextMissing) {
    const result = flow.execute(collected);
    return {
      ...result,
      memoryPatch: { ...(result.memoryPatch ?? {}), pendingFlow: null },
      done: true,
    };
  }

  const ask = askNextSlot(flow, collected);
  const progress = `(${flow.slots.findIndex((s) => s.name === nextMissing.name) + 1}/${flow.slots.length}) `;
  return {
    reply: `${progress}${ask.reply}`,
    widgets: ask.widgets,
    memoryPatch: {
      pendingFlow: {
        ...pending,
        collected,
        currentSlot: nextMissing.name,
      },
    },
  };
}

// ------------------------------------------------------------
// Start a new flow given a trigger message
// ------------------------------------------------------------
export function startFlow(flow: FlowDef, message: string): FlowResponse {
  // Try to opportunistically pre-fill slots from the trigger message
  const collected: Record<string, unknown> = {};
  for (const slot of flow.slots) {
    const v = slot.extract(message);
    if (v !== undefined && (!slot.validate || slot.validate(v) === null)) {
      collected[slot.name] = v;
    }
  }

  const nextMissing = flow.slots.find((s) => collected[s.name] === undefined);
  if (!nextMissing) {
    // Everything pre-filled → execute immediately
    const result = flow.execute(collected);
    return { ...result, memoryPatch: { ...(result.memoryPatch ?? {}), pendingFlow: null }, done: true };
  }

  const ask = askNextSlot(flow, collected);
  const progress = `(${flow.slots.findIndex((s) => s.name === nextMissing.name) + 1}/${flow.slots.length}) `;
  return {
    reply: `${flow.startReply}\n${progress}${ask.reply}`,
    widgets: ask.widgets,
    memoryPatch: {
      pendingFlow: {
        id: flow.id,
        collected,
        currentSlot: nextMissing.name,
        startedAt: Date.now(),
      },
    },
  };
}
